import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

//Api
import { supplierApi } from "@/main/api";
import { DeliveryWithoutLogisticCompanysRequest, SaveSupplierConfigurationRequest } from "@/main/models/request";

//Components
import Button from '@/main/components/shared/button/button';
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import DistributionInfo from "@/main/screens/registration/supplier/supplierRegisterScreen/distributionInfo";
import DeliveryLogistic from "../deliveryLogistic/deliveryLogistic";
import SectionParameter from "@/main/components/shared/tabs/insurer/tabSettingRegister/sectionParameter/sectionParameter";
import { checkParametersChange } from "@/main/components/shared/tabs/insurer/tabSettingRegister/utils";
import useAsyncModalConfirmReason from '@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { toBase64 } from '@/main/services/utilsFile';

//Service
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { ParameterTypeValueEnum } from "@/main/services/enum/ParameterTypeValueEnum";

//Utils
import { getTranslateByKey } from "@/main/services/utils";
import { arraysIsEqual } from '@/main/services/utilsArray';

//Type
import { ParameterTypesResponse } from "@/main/models/response/parameterTypesResponse";
import { KeyValue } from "@/main/models/common";
import { IDataDeliveryLogistic } from "../deliveryLogistic/types";

//Styles
import * as SP from '@/main/components/shared/styles';
import * as S from './styles';

interface ITabSettings {
  parameterTypes: ParameterTypesResponse[],
  watcherCountry: KeyValue | null,
  dataDitribution: string[],
  savedDataDitribution: string[],
  savedDataDeliverLogistic: IDataDeliveryLogistic[],
  setDataDitribution: React.Dispatch<React.SetStateAction<string[]>>,
  setParameterTypes: React.Dispatch<React.SetStateAction<ParameterTypesResponse[]>>
  updateSavedDataDitribution: (data: string[]) => void
  updateSavedDataDeliverLogistic: (data: IDataDeliveryLogistic[]) => void
}

function SettingsSupplier({
  parameterTypes,
  watcherCountry,
  dataDitribution,
  savedDataDitribution,
  savedDataDeliverLogistic,
  setDataDitribution,
  setParameterTypes,
  updateSavedDataDitribution = () => { },
  updateSavedDataDeliverLogistic = () => { }
}: ITabSettings) {
  const lowercaseValues = (parameterTypes: ParameterTypesResponse[]) => {
    return parameterTypes.map(data => ({
      ...data,
      parameters: data.parameters.map(parameter => {
        if (data.idParameterTypeValue === ParameterTypeValueEnum.Boolean) {
          return {
            ...parameter,
            value: parameter.value.toLowerCase()
          }
        } else {
          return {
            ...parameter
          }
        }
      })
    }));
  };

  // Reducers/Hooks
  const navigate = useNavigate();
  const [searchParams, _] = useSearchParams();
  const guidSupplierParams = searchParams.get('supplierGuid');

  // States
  const [dataDeliverLogistic, setDataDeliverLogistic] = useState<IDataDeliveryLogistic[]>([]);
  const [isLoadingSaveSettings, setIsLoadingSaveSettings] = useState<boolean>(false);
  const [dataUserParameters, setDataUserParameters] = useState<ParameterTypesResponse[]>(lowercaseValues(parameterTypes));
  const [dataParametersScreen, setDataParametersScreen] = useState<ParameterTypesResponse[]>(lowercaseValues(parameterTypes));
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

  const formatDataDistributionRegion = (distributionRegions: string[]) =>
    distributionRegions.map(distributionRegion => ({
      guidDistributionRegion: distributionRegion
    }));
  const formatDataParameters = (parameters: ParameterTypesResponse[]): { guid: string, value: string }[] => {
    const requestSaveParameters: { guid: string, value: string }[] = [];

    parameters.map((x) => {
      x.parameters.map((y) => {
        if (y.idParameterType === ParameterTypeEnum.CarrierCollectsPackage) {
          const findParameterTypePackagesSentByTheCarrier = dataParametersScreen.find(
            param => param.idParameterType === ParameterTypeEnum.AlwaysEnsurePackagesSentByTheCarrier
          );
          const findValueParameterTypePackagesSentByTheCarrier = findParameterTypePackagesSentByTheCarrier?.parameters.find(
            param => param.idParameterType === ParameterTypeEnum.AlwaysEnsurePackagesSentByTheCarrier
          );
          if (findValueParameterTypePackagesSentByTheCarrier?.value.toLowerCase() === 'false') {
            return requestSaveParameters.push({
              guid: y.guid,
              value: 'False'
            });
          }
        }
        return requestSaveParameters.push({
          guid: y.guid,
          value: y.value
        });
      });
    });
    return requestSaveParameters;
  }

  function handleBack() {
    navigate(-1);
  }

  async function handleSaveSettings() {
    const haveDistribution = dataDitribution.length > 0;
    if (!haveDistribution) {
      toast.error(getTranslateByKey('information.mustHaveAtLeastDisitribution', true));
      return false;
    }

    const resultConfirm = await getConfirmationReason(
      getTranslateByKey('information.confirmChangeTitle'),
      ReasonTypeEnum.ChangeCompany
    );

    if (!resultConfirm.confirmed) return;

    let filesRequest = undefined;
    if (resultConfirm.files && resultConfirm.files.length > 0) {
      filesRequest = await Promise.all(
        resultConfirm.files.map(async item => {
          const base64DataUrl = await toBase64(item);
          return {
            fileName: item.name,
            contentType: item.type,
            fileBase64: base64DataUrl as string
          };
        })
      );
    }

    setIsLoadingSaveSettings(true);
    const formattedDitributionRegion = formatDataDistributionRegion(
      dataDitribution
    );
    const requestSaveParameters = formatDataParameters(dataParametersScreen);

    const requestDeliverLogistic: DeliveryWithoutLogisticCompanysRequest[] = dataDeliverLogistic.map((item) => ({
      zipCodeEnd: item.finalZipCode,
      zipCodeStart: item.initialZipCode,
      daysToDeliveryPart: Number(item.daysDelivery),
      idDeliveryType: item.idDeliveryType
    }));

    const request: SaveSupplierConfigurationRequest = {
      guidSupplier: String(guidSupplierParams),
      parameters: requestSaveParameters,
      supplierDistributionRegions: formattedDitributionRegion,
      deliveryWithoutLogisticCompanys: requestDeliverLogistic,
      changeJustification: {
        guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
        reasonDescription: resultConfirm?.observation || null,
        filesUpload: filesRequest
      }
    }

    supplierApi.saveSupplierConfiguration(request)
      .then(() => {
        updateSavedDataDitribution(dataDitribution)
        updateSavedDataDeliverLogistic(dataDeliverLogistic)
        toast.success(
          getTranslateByKey('information.supplierSettingsSavedSuccessfully')
        );
        setParameterTypes(dataParametersScreen);
        setDataUserParameters(lowercaseValues(dataParametersScreen));
      })
      .finally(() => setIsLoadingSaveSettings(false));
  }

  useEffect(() => {
    setDataDeliverLogistic(savedDataDeliverLogistic)
  }, [savedDataDeliverLogistic])

  const haveChangeDataDeliverLogistic = !arraysIsEqual<IDataDeliveryLogistic>(savedDataDeliverLogistic, dataDeliverLogistic, 'concatString')
  const haveChangeDataDitribution = !arraysIsEqual(savedDataDitribution, dataDitribution)
  const disabledButtonSave = !(checkParametersChange(dataParametersScreen, dataUserParameters) || haveChangeDataDitribution || haveChangeDataDeliverLogistic);

  return (
    <>
      <S.Container>
        <S.ContainerSection>
          <SP.HeaderText>
            {getTranslateByKey('information.registerInsurerParameters')}
          </SP.HeaderText>

          <SectionParameter
            dataParameterAPI={parameterTypes}
            dataParametersScreen={dataParametersScreen}
            setDataParametersScreen={setDataParametersScreen}
          />

        </S.ContainerSection>
        <DistributionInfo
          watcherCountry={watcherCountry}
          dataDitribution={dataDitribution}
          setDataDitribution={setDataDitribution}
        />
        <DeliveryLogistic
          dataDeliverLogistic={dataDeliverLogistic}
          setDataDeliverLogistic={setDataDeliverLogistic}
        />

        <S.FooterButtons>
          <SecondaryButton
            type="button"
            onClick={handleBack}
            id='id-insurerBodyshopTabSettings-button-back'
          >
            {getTranslateByKey('information.back')}
          </SecondaryButton>
          <Button
            id='id-insurerBodyshopTabSettings-button-save'
            type="submit"
            disabled={disabledButtonSave || isLoadingSaveSettings}
            isLoading={isLoadingSaveSettings}
            onClick={handleSaveSettings}
          >
            {getTranslateByKey('information.save')}
          </Button>
        </S.FooterButtons>
        <ModalConfirmationReason closeByOverlay={false} />
      </S.Container>

    </>
  )
}
export default SettingsSupplier;